import { useMemo, useState } from "react";
import { Sliders, CheckCircle, ArrowRight, Question } from "@phosphor-icons/react";

const IGNORE = "ignore";

export default function CsvMappingCard({ card, onConfirm }) {
  const targets = card.targets || [];
  const exact = card.exact || {};
  const suggested = card.suggested || {};
  const labelOf = useMemo(() => {
    const m = {};
    for (const t of targets) m[t.key] = t.label;
    return m;
  }, [targets]);

  // Right bucket = headers whose name exactly matches one of our export field names.
  const knownHeaders = card.headers.filter((h) => exact[h]);
  // Left bucket = everything else (synonym/fuzzy matches + unknown headers).
  const unknownHeaders = card.headers.filter((h) => !exact[h]);

  const [map, setMap] = useState(() => {
    const m = {};
    for (const h of unknownHeaders) m[h] = suggested[h] || IGNORE;
    return m;
  });
  const [busy, setBusy] = useState(false);

  const setHdr = (h, t) => setMap((p) => ({ ...p, [h]: t }));

  const idCovered =
    knownHeaders.some((h) => exact[h] === "college_code" || exact[h] === "college_name") ||
    unknownHeaders.some((h) => map[h] === "college_code" || map[h] === "college_name");

  const finish = async () => {
    setBusy(true);
    const clean = {};
    for (const h of knownHeaders) clean[h] = exact[h];
    for (const h of unknownHeaders) if (map[h] && map[h] !== IGNORE) clean[h] = map[h];
    await onConfirm(clean);
    setBusy(false);
  };

  return (
    <div className="flex gap-3 animate-fade-in" data-testid={`csv-mapping-card-${card.mid}`}>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6043EF] to-[#8B5CF6] flex items-center justify-center shrink-0">
        <Sliders size={18} weight="fill" className="text-white" />
      </div>
      <div className="max-w-[94%] w-full border border-[#6043EF]/25 bg-gradient-to-br from-[#6043EF]/5 to-white p-4 rounded-3xl rounded-tl-md shadow-sm">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[13px] font-black text-[#09090B] truncate">{card.filename}</span>
        </div>
        <p className="text-[11px] text-zinc-500 mb-3">
          Map the columns on the left to a field, then confirm. Columns on the right already match our fields.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* LEFT — unknown / fuzzy columns to map */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3" data-testid="mapping-unknown-bucket">
            <div className="flex items-center gap-1.5 mb-2">
              <Question size={14} weight="bold" className="text-amber-600" />
              <span className="text-[10px] font-black uppercase tracking-[0.14em] text-amber-700">
                Columns to map ({unknownHeaders.length})
              </span>
            </div>
            {unknownHeaders.length === 0 ? (
              <p className="text-[11px] text-zinc-400 italic py-2">Nothing to map — all columns recognised.</p>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-auto pr-1">
                {unknownHeaders.map((h) => (
                  <div key={h} className="flex items-center gap-2" data-testid={`map-row-${h}`}>
                    <span className="text-[11px] font-mono text-[#09090B] truncate flex-1" title={h}>{h}</span>
                    <ArrowRight size={12} className="text-amber-400 shrink-0" />
                    <select
                      value={map[h]}
                      onChange={(e) => setHdr(h, e.target.value)}
                      data-testid={`map-select-${h}`}
                      className={`h-7 w-40 shrink-0 rounded-md border text-[11px] px-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40 ${
                        map[h] === IGNORE ? "border-zinc-300 text-zinc-400" : "border-[#6043EF]/50 text-[#09090B] font-semibold"
                      }`}
                    >
                      <option value={IGNORE}>— Ignore —</option>
                      {targets.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — recognised columns (exact export-name match) */}
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="mapping-known-bucket">
            <div className="flex items-center gap-1.5 mb-2">
              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
              <span className="text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">
                Recognised ({knownHeaders.length})
              </span>
            </div>
            {knownHeaders.length === 0 ? (
              <p className="text-[11px] text-zinc-400 italic py-2">No columns matched our field names exactly.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-72 overflow-auto">
                {knownHeaders.map((h) => (
                  <span
                    key={h}
                    data-testid={`known-chip-${h}`}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white border border-emerald-300 text-[11px]"
                    title={`${h} → ${labelOf[exact[h]] || exact[h]}`}
                  >
                    <span className="font-mono text-[#09090B] truncate max-w-[110px]">{h}</span>
                    <span className="text-emerald-600 font-bold">{labelOf[exact[h]] || exact[h]}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={finish}
            disabled={busy || !idCovered}
            data-testid="map-confirm-btn"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#09090B] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-black disabled:opacity-40"
          >
            <CheckCircle size={14} weight="bold" /> {busy ? "Processing…" : "Confirm & preview"}
          </button>
          {!idCovered && (
            <span className="text-[11px] text-rose-600 font-semibold" data-testid="map-id-warning">
              Map a college code or name column to continue
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
